import React, { FC } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Check } from "lucide-react";
import { Loader } from "@/components/ui/loader";

import { PromptLibraryItem, PromptLibraryItemSource } from "@/modules/promptLibrary/promptLibrary.types";
import { FormInput } from "@/components/forms/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  useCreatePromptLibraryItemsMutation,
  useGetSuggestedPromptLibraryItemNameQuery,
  useUpdatePromptLibraryItemsMutation,
} from "@/modules/promptLibrary/promptLibrary.api";
import { FormTextArea } from "@/components/forms/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "react-toastify";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";

type Props =
  | {
      isOpen: boolean;
      mode: "add";
      setIsOpen: (isOpen: boolean) => void;
      item?: Pick<PromptLibraryItem, "prompt" | "source">;
    }
  | {
      isOpen: boolean;
      item: PromptLibraryItem;
      mode: "edit";
      setIsOpen: (isOpen: boolean) => void;
    };

interface FormInputs {
  name: string;
  prompt: string;
  notes: string;
  source: PromptLibraryItemSource;
  visibility: boolean;
}

export const ModifyNewPromptLibraryItemDialog: FC<Props> = ({ isOpen, setIsOpen, item, mode }) => {
  const [createPromptLibraryItem] = useCreatePromptLibraryItemsMutation();
  const [updatePromptLibraryItemsMutation] = useUpdatePromptLibraryItemsMutation();
  const { organization } = useAuthenticatedUser();
  // An existing Open Chat prompt keeps its source after the organization loses the feature.
  const canChooseOpenChat = organization.features.includes("open_chat") || item?.source === "open_chat";

  const methods = useForm<FormInputs>({
    defaultValues: {
      name: item && "name" in item ? item.name : "",
      prompt: item?.prompt,
      notes: item && "notes" in item ? item.notes : "",
      source: item && "source" in item ? item.source : "qa",
      visibility: item && "visibility" in item ? item.visibility === "public" : false,
    },
  });
  const { data, isLoading } = useGetSuggestedPromptLibraryItemNameQuery(item?.prompt, {
    skip: !item?.prompt || mode !== "add",
  });

  const { setValue } = methods;
  React.useEffect(() => {
    if (!data || mode !== "add") {
      return;
    }
    setValue("name", data);
  }, [data, mode, setValue]);

  const onSubmit = async (payload: FormInputs) => {
    if (mode === "add") {
      await createPromptLibraryItem({ ...payload, visibility: payload.visibility ? "public" : "private" });
    } else {
      await updatePromptLibraryItemsMutation({
        id: item.id,
        ...payload,
        visibility: payload.visibility ? "public" : "private",
      });
    }

    methods.reset();
    toast.success("Item has been saved successfully");

    setIsOpen(false);
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <div className="flex h-full w-full items-center justify-center">
            <Loader className="h-8 w-8" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <FormProvider {...methods}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-h-[100vh] max-w-xl overflow-y-scroll">
          <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogHeader className="mb-5">
              <DialogTitle>{mode === "add" ? "Add" : "Edit"} Prompt</DialogTitle>
            </DialogHeader>

            <div className="mb-6 flex flex-col gap-4">
              <div>
                <label htmlFor="prompt" className="mb-3 text-sm font-medium">
                  Prompt
                </label>
                <FormTextArea
                  maxRows={16}
                  {...methods.register("prompt", { required: "This field is required" })}
                  placeholder="Type prompt improve"
                  name="prompt"
                />
              </div>

              <div className="flex gap-x-4">
                <div className="w-full">
                  <label htmlFor="name" className="mb-3 text-sm font-medium">
                    Name
                  </label>
                  <FormInput
                    {...methods.register("name", { required: "This field is required" })}
                    name="name"
                    placeholder="Enter name"
                  />
                </div>

                <div className="w-full">
                  <label htmlFor="source" className="mb-3 text-sm font-medium">
                    Source
                  </label>
                  <select
                    {...methods.register("source", { required: true })}
                    disabled={!!item?.source && mode === "add"}
                    className="block h-[40px] w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
                  >
                    <option value="qa">Q&A</option>
                    {canChooseOpenChat && <option value="open_chat">Open Chat</option>}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="notes" className="mb-3 text-sm font-medium">
                  Notes
                </label>
                <FormTextArea
                  {...methods.register("notes", { required: "This field is required" })}
                  placeholder="Add any additional notes here..."
                  name="notes"
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="visibility"
                  className="mt-0.5 h-5 w-5 rounded border border-gray-300 bg-white"
                  defaultChecked={item && "visibility" in item ? item.visibility === "public" : false}
                  onCheckedChange={checked => methods.setValue("visibility", !!checked)}
                >
                  <Check className="text-white" size={14} />
                </Checkbox>
                <Label htmlFor="full_context" className="text-sm font-normal">
                  Public prompt
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={methods.formState.isSubmitting}>
                {methods.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};
